import { DashboardDateRangePicker } from '@/components/dashboard/DashboardDateRangePicker';
import {
  snapshotRangesValid,
  type SnapshotRangeState,
} from '@/components/snapshot/SnapshotDateRangePicker';
import { cn } from '@/utils/cn';
import {
  getPriorPeriod,
  isRangeValid,
  matchesWorkspaceDatePreset,
  resolveWorkspaceDatePreset,
  WORKSPACE_DATE_PRESETS,
  type WorkspaceDatePresetId,
} from '@/api/reportDateRanges';

export function WorkspaceDateRangeControls({
  value,
  onChange,
  compact = false,
  showCompare = true,
  toDate,
}: {
  value: SnapshotRangeState;
  onChange: (next: SnapshotRangeState) => void;
  /** Horizontal layout for TopBar */
  compact?: boolean;
  showCompare?: boolean;
  toDate?: Date;
}) {
  const activePresetId = WORKSPACE_DATE_PRESETS.find((preset) =>
    matchesWorkspaceDatePreset(preset.id, value.current, value.previous)
  )?.id;

  const applyPreset = (id: string) => {
    const { current, previous } = resolveWorkspaceDatePreset(id as WorkspaceDatePresetId);
    onChange({ ...value, current, previous, compareEnabled: true });
  };

  const currentInvalid = !isRangeValid(value.current);
  const previousInvalid = !isRangeValid(value.previous);

  return (
    <div
      className={cn(
        'flex',
        compact ? 'flex-nowrap items-center gap-4' : 'flex-wrap items-end justify-end gap-3'
      )}
    >
      <div className={cn('flex min-w-0', compact ? 'items-center gap-2' : 'flex-col gap-1')}>
        <span className="shrink-0 whitespace-nowrap text-body-sm text-foreground-muted">
          Date range
        </span>
        <DashboardDateRangePicker
          startDate={value.current.startDate}
          endDate={value.current.endDate}
          onChange={(range) =>
            onChange({
              ...value,
              current: range,
              previous: getPriorPeriod(range),
              compareEnabled: true,
            })
          }
          invalidRange={
            showCompare
              ? currentInvalid || !snapshotRangesValid({ ...value, compareEnabled: true })
              : currentInvalid
          }
          variant="default"
          showHelp={false}
          presets={WORKSPACE_DATE_PRESETS}
          activePresetId={activePresetId}
          onPresetSelect={applyPreset}
          toDate={toDate}
        />
      </div>

      {showCompare ? (
        <div className={cn('flex min-w-0', compact ? 'items-center gap-2' : 'flex-col gap-1')}>
          <span className="shrink-0 whitespace-nowrap text-body-sm text-foreground-muted">
            Compare to
          </span>
          <DashboardDateRangePicker
            startDate={value.previous.startDate}
            endDate={value.previous.endDate}
            onChange={(range) => onChange({ ...value, previous: range, compareEnabled: true })}
            invalidRange={previousInvalid}
            variant="default"
            showHelp={false}
            toDate={toDate}
          />
        </div>
      ) : null}
    </div>
  );
}
